import store from '@advisor/store';
import RwAdvisorService from '@advisor/rwSdk/AdvisorService';
import RwOrchestrateService from '@advisor/rwSdk/OrchestrateService';
import AdvCommonUtilMethods from '@advisor/util/AdvCommonUtilMethods';
import OnboardingRiaService from '@advisor/services/OnboardingRia';
import exceptionHandler from '@advisor/util/exceptionHandler';

import {
  RIA_ACCOUNT_STATUS,
  PLATFORM_AREA,
  COMPONENT_ID,
  RIA_COMPONENT,
  ACCOUNT_REQUEST_STATUS,
  NOTIFICATION_TYPE,
  LOADER_TYPE,
} from '@advisor/util/constants';

export default [
  {
    path: '/',
    redirect: '/advisorlogin',
  },
  {
    path: '/advisorlogin',
    name: 'AdvisorLogin',
    meta: {
      unguarded: true,
      module: 'ria-onboarding',
      platformArea: PLATFORM_AREA.COMMON,
    },
    components: {
      header: null,
      default: () => import('@advisor/components/dashboard/AdvisorLogin.vue'),
    },
    beforeEnter: (to, from, next) => {
      next();
    },
  },
  {
    path: '/register',
    name: 'OnboardRia',
    meta: {
      unguarded: true,
      module: 'ria-onboarding',
      platformArea: PLATFORM_AREA.COMMON,
    },
    components: {
      header: null,
      default: () => import('@advisor/components/onboardingRia/Register'),
    },
    beforeEnter: (to, from, next) => {
      const {allowRegister} = store.state.settings.ria;

      if (allowRegister === 0) {
        window.location.href = `${window.BASE_URL}`;

        return;
      }

      next();
    },
  },
  {
    path: '/register/verify',
    name: 'VerifyFirm',
    meta: {
      unguarded: true,
      module: 'ria-onboarding',
      // FIXME: the line below has been added for extra validation, can be deleted if not really needed.
      platformArea: PLATFORM_AREA.COMMON,
    },
    components: {
      // eslint-disable-next-line no-undefined
      header: null,
      default: () => import('@advisor/components/onboardingRia/VerifyFirm'),
    },
    beforeEnter: (to, from, next) => {
      const firm = store.state.onboardingRia.onboardedRia;
      const {riaFirm} = firm;

      if (riaFirm?.email1) {
        next();

        return;
      }

      window.location.href = `${window.BASE_URL}`;
    },
  },
  {
    path: '/advisor/activate-advisor-onboard',
    name: 'ActivateAdvisorOnboard',
    meta: {
      unguarded: true,
      platformArea: PLATFORM_AREA.COMMON,
    },

    // eslint-disable-next-line max-lines-per-function, max-statements
    beforeEnter: async (to) => {
      const {riaUserId} = to.query;
      const emailCode = to.query.email_code;

      try {
        const advData = await RwAdvisorService.getRiaUserFromRiaUserId({
          riaUserId,
        }).catch((error) => {
          throw error;
        });

        const email = advData.contactInfo.email1;

        // if account status ===1 , then account is already set, redirecting to login
        if (parseInt(advData.accountStatus, 10) === RIA_ACCOUNT_STATUS.ACTIVATED) {
          window.location.href = `${window.BASE_URL}?emailVerified=true&email=${email}`;

          return;
        }

        const confirmEmailResponse = await RwOrchestrateService.confirmEmail({
          riaUserId,
          emailVerificationCode: emailCode,
        });

        if (confirmEmailResponse.error) {
          throw confirmEmailResponse.error;
        }

        if (!confirmEmailResponse.verified) {
          throw confirmEmailResponse.verifications;
        }

        const payload = {
          riaUser: {riaUserId},
          verifyUserRequest: {emailVerificationCode: emailCode},
          workflows: ['confirmRiaEmailVerification'],
        };

        const response = await OnboardingRiaService.onboardingRia(payload);
        const {emailVerified} = response?.data?.data?.riaUser;

        if (emailVerified === '1') {
          store.dispatch('notification/setMessage', {
            type: NOTIFICATION_TYPE.SUCCESS,
            text: `Email address verified successfully`,
            timeout: 4,
          });
          window.location.href = `${window.BASE_URL}`;

          return;
        }

        window.location.href = `${window.BASE_URL}?emailVerified=false`;
      } catch {
        window.location.href = `${window.BASE_URL}?emailVerified=false`;
      }
    },
  },
  {
    // Initiate Bank Link - Landing
    path: '/advisor/client/initiate-bank-link/:clUserId?',
    name: 'InitiateBankLink',
    component: () => import('@advisor/components/clients/clientDetails/transactions/InitiateBankLink'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    // eslint-disable-next-line require-await
    beforeEnter: async (to, from, next) => {
      if (!AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next)) {
        return;
      }

      if (from.params.clientId || to.params.clUserId) {
        // eslint-disable-next-line no-param-reassign
        to.params.clUserId = to.params.clUserId ? to.params.clUserId : from.params.clientId;
      }

      next();
    },
  },
  {
    // Money Transfer Wizard - Landing
    path: '/advisor/client/initiate-transfer-money/:clUserId?',
    name: 'MoneyTransferLanding',
    component: () => import('@advisor/components/clients/transferMoney/Landing'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    // eslint-disable-next-line require-await
    beforeEnter: async (to, from, next) => {
      // If riaUser role is viewOnly
      if (!AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next)) {
        return;
      }

      if (from.params.clientId || to.params.clUserId) {
        // eslint-disable-next-line no-param-reassign
        to.params.clUserId = to.params.clUserId ? to.params.clUserId : from.params.clientId;
      }

      next();
    },
  },
  {
    // ActivityTab
    path: '/advisor/activity/:section?',
    name: 'ActivityTab',
    component: () => import('@advisor/components/activity/ActivityTab'),
    meta: {
      breadCrumbs: {
        to: '/advisor/activity',
        text: '',
        title: 'Activity',
        componentId: COMPONENT_ID.ACTIVITY,
      },
      riaComponent: RIA_COMPONENT.ACTIVITY,
      platformArea: PLATFORM_AREA.ACTIVITY,
      visibilityComponentId: COMPONENT_ID.ACTIVITY,
    },
  },
  {
    // Money Transfer Wizard - Wizard
    path: '/advisor/client/transfer-money/:directionName?/:clUserId?/:transferId?',
    name: 'MoneyTransfer',
    component: () => import('@advisor/components/clients/transferMoney/Wizard'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    beforeEnter: async (to, from, next) => {
      // If riaUser role is viewOnly
      if (!AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next)) {
        return;
      }

      try {
        await Promise.all([
          // Load the first step
          store.dispatch('moneyTransfer/setActiveTab', {
            activeTab: 0,
          }),
          // Load client details by cluserId
          store.dispatch('client/getClientWithSubResources', {
            id: to.params.clUserId,
            subResources: 'segment',
          }),
          // Load the brokage account
          store.dispatch('moneyTransfer/getBrokerageAccounts', {
            clUserId: to.params.clUserId,
          }),
        ]).then(() => next());
      } catch {
        const msg = {
          type: NOTIFICATION_TYPE.ERROR,
          text: 'Error: Money Transfer details could not be retrieved.',
          timeout: 5,
        };

        store.dispatch('notification/setMessage', msg);
      }
      next();
    },
  },
  {
    // Clients: Onboarding
    path: '/advisor/client/onboard/',
    name: 'ClientOnboarding',
    component: () => import('@advisor/components/clients/onBoarding/PersonalInfo'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    beforeEnter: (to, from, next) => AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next),
  },
  {
    // Clients: Onboarding - Thank You
    path: '/advisor/client/onboard/:clUserId/thankyou',
    name: 'ThankYou',
    component: () => import('@advisor/components/clients/onBoarding/ThankYou'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    beforeEnter: (to, from, next) => AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next),
  },
  {
    // Clients: Open Account
    path: '/advisor/client/open-new-account/:clUserId?',
    name: 'openAccountExist',
    component: () => import('@advisor/components/clients/openAccount/Index'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    beforeEnter: async (to, from, next) => {
      // get the permissions and redirect if view-only
      if (!AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next)) {
        return;
      }

      if (from.params.clientId || to.params.clUserId) {
        // eslint-disable-next-line no-param-reassign
        to.params.clUserId = to.params.clUserId ? to.params.clUserId : from.params.clientId;
      }

      try {
        const custodians = await store.dispatch('custodian/getCustodianTypes');

        if (!_.isArray(custodians) || _.isEmpty(custodians)) {
          store.dispatch('notification/setMessage', {
            type: NOTIFICATION_TYPE.WARNING,
            text: `A custodian and rep code must be established for your firm before opening an account.`,
            timeout: 5,
          });

          next({name: 'Overview'});

          return;
        }
      } catch (error) {
        exceptionHandler.handleGenericException('index.getCustodianTypes', error);
      }
      next();
    },
  },
  {
    // Clients: Onboarding - Wizard
    path: '/advisor/client/open-account/:clUserId/:custodianId/:requestId?',
    name: 'clientOpenAccount',
    component: () => import('@advisor/components/clients/onBoarding/Wizard'),
    meta: {
      riaComponent: RIA_COMPONENT.CLIENTS,
      platformArea: PLATFORM_AREA.CLIENTS,
      visibilityComponentId: COMPONENT_ID.CLIENTS,
    },
    // eslint-disable-next-line complexity, max-lines-per-function, max-statements
    beforeEnter: async (to, from, next) => {
      if (!AdvCommonUtilMethods.clientViewOnlyCheck(to, from, next)) {
        return;
      }

      // Set the global loader for openAccount page
      store.dispatch('ux/setGlobalAdvLoader', {
        type: LOADER_TYPE.STANDARD,
        loading: true,
      });

      // Callback for resetting global loader
      const resetDefaultGlobalAdvLoader = () => {
        store.dispatch('ux/setGlobalAdvLoader', null);
      };

      try {
        await Promise.all([
          store.dispatch('client/setClientOnBoardingData', null),
          store.dispatch('custodian/getCustodianTypes'),
          store.dispatch('client/fetchExistingClientOnboardingData', {
            clUserId: to.params.clUserId,
          }),
        ]);
      } catch (error) {
        exceptionHandler.handleGenericException('index.ClientsOnboardingWizard', error);
      }

      if (to.params.requestId) {
        try {
          const objAccountRequestDetails = await store.dispatch('custodian/getAccountRequestDetails', {
            requestId: to.params.requestId,
          });

          if (
            objAccountRequestDetails.accountStatus === ACCOUNT_REQUEST_STATUS.PENDING_VERIFICATION &&
            objAccountRequestDetails.requestInitiator === 'advisordashboard'
          ) {
            store.dispatch('notification/setMessage', {
              type: NOTIFICATION_TYPE.ERROR,
              text: 'This account request has been moved to the client to complete.',
              timeout: 3,
            });

            resetDefaultGlobalAdvLoader();

            next({name: 'Overview'});

            return;
          }

          if (
            objAccountRequestDetails.disclosures.exchangeEntity === null ||
            objAccountRequestDetails.disclosures.politicalExposureDetail === null ||
            objAccountRequestDetails.disclosures.publicCompanyHolder === null
          ) {
            await store.dispatch('client/getDefaultDisclosures', {
              clUserId: to.params.clUserId,
            });
          }

          await store.dispatch('custodian/fetchExistingAccountDetails', objAccountRequestDetails);
        } catch (error) {
          exceptionHandler.handleGenericException('index.fetchExistingAccountDetails', error);
        }

        resetDefaultGlobalAdvLoader();

        next();

        return;
      }

      const {clUserId, custodianId} = to.params;

      try {
        const response = await store.dispatch('client/getActiveAccountRequests', {
          clUserId,
          custodianId,
          requestInitiatedFrom: 'advisordashboard',
        });

        if (response.data.error) {
          throw response.data.error;
        }

        const accRequest = response.data.data.length > 0 ? response.data.data.shift() : null;

        // If requestId found populate in the route, otherwise stay here
        if (accRequest?.id) {
          try {
            const objAccountRequestDetails = await store.dispatch('custodian/getAccountRequestDetails', {
              requestId: accRequest.id,
            });

            await store.dispatch('custodian/fetchExistingAccountDetails', objAccountRequestDetails);
          } catch (error) {
            exceptionHandler.handleGenericException('index.fetchExistingAccountDetails', error);
          }

          const requestId = accRequest.id;

          resetDefaultGlobalAdvLoader();

          next({
            name: 'clientOpenAccount',
            params: {
              clUserId,
              custodianId,
              requestId,
            },
          });

          return;
        }

        await store.dispatch('custodian/fetchDefaultDividendSetting', clUserId);

        resetDefaultGlobalAdvLoader();

        next();

        // eslint-disable-next-line no-useless-return
        return;
      } catch (error) {
        exceptionHandler.handleGenericException('index.clientOpenAccount', error);
      }
    },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('@advisor/components/AdvMaintenancePage'),
  },
  {
    // 404 Page
    path: '*',
    name: '404',
    component: () => import('@advisor/components/Adv404'),
    meta: {
      platformArea: PLATFORM_AREA.COMMON,
    },
  },
  {
    // 404 Page
    path: '/advisor/404',
    name: 'App404',
    component: () => import('@advisor/components/Adv404'),
    meta: {
      platformArea: PLATFORM_AREA.COMMON,
    },
  },
  {
    // 403 Page
    path: '/advisor/403',
    name: 'App403',
    component: () => import('@advisor/components/Adv403'),
    meta: {
      unguarded: true,
      // FIXME: the line below has been added for extra validation, can be deleted if not really needed.
      platformArea: PLATFORM_AREA.COMMON,
    },
  },
  {
    // 401 Page
    path: '/advisor/401',
    name: 'App401',
    component: () => import('@advisor/components/Adv401'),
    meta: {
      platformArea: PLATFORM_AREA.COMMON,
    },
  },
  {
    // Risk Tolerance Setting
    path: '/advisor/risk-tolerance',
    name: 'risk-tolerance-setting',
    component: () => import('@advisor/components/settings/RiskToleranceSetting'),
    meta: {
      breadCrumbs: {
        to: '',
        text: 'Settings',
        title: 'Risk Tolerance',
        componentId: COMPONENT_ID.RISK_TOLERANCE,
      },
      platformArea: PLATFORM_AREA.RISKTOLERANCE,
      riaComponent: RIA_COMPONENT.SETTINGS_RISK_TOLERANCE,
      visibilityComponentId: COMPONENT_ID.RISK_TOLERANCE,
    },
  },
  {
    // Supervision Action Required
    path: '/advisor/supervisor-approval/supervisor-action-required',
    name: 'supervisor-action-required',
    component: () => import('@advisor/components/supervisorApproval/supervisorApproval'),
    meta: {
      breadCrumbs: {
        to: '/advisor/supervisor-approval',
        text: '',
        title: 'Account Opening Supervision',
        componentId: COMPONENT_ID.ADO_SUPERVISOR_APPROVAL,
      },
      riaComponent: RIA_COMPONENT.ADO_SUPERVISOR_APPROVAL,
      platformArea: PLATFORM_AREA.ADO_SUPERVISOR_APPROVAL,
      visibilityComponentId: COMPONENT_ID.ADO_SUPERVISOR_APPROVAL,
    },
  },
];
